import { useState, useEffect } from 'react'
import { Search, UserPlus } from 'lucide-react'
import { listUsers, type User } from '../lib/api'

interface UserSearchPickerProps {
  excludeIds?: number[]
  onPick: (userId: number) => void
  label?: string
}

/** Type-ahead user picker: server-side search (listUsers(query)) so it scales
 * to hundreds of users instead of dumping them all into a dropdown. */
export function UserSearchPicker({ excludeIds = [], onPick, label = 'Add user' }: UserSearchPickerProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<User[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    const q = query.trim()
    if (q.length < 1) {
      setResults([])
      return
    }
    let cancelled = false
    setLoading(true)
    const t = setTimeout(async () => {
      try {
        const users = await listUsers(q)
        if (!cancelled) setResults(users)
      } catch {
        if (!cancelled) setResults([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [query, open])

  const visible = results.filter((u) => !excludeIds.includes(u.id)).slice(0, 25)

  return (
    <div className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 px-3 py-1 rounded text-sm bg-muted text-muted-foreground hover:bg-muted/80 transition-colors"
      >
        <UserPlus className="w-3 h-3" /> {label}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 w-72 max-h-80 overflow-y-auto rounded-lg border border-border bg-card shadow-xl p-1">
            <div className="flex items-center gap-2 px-2 py-1 border-b border-border mb-1">
              <Search className="w-4 h-4 text-muted-foreground" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full bg-transparent text-sm outline-none"
              />
            </div>
            {query.trim().length < 1 ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">Start typing to search users.</p>
            ) : loading ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">Searching…</p>
            ) : visible.length === 0 ? (
              <p className="px-2 py-2 text-sm text-muted-foreground">No matching users.</p>
            ) : (
              visible.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => {
                    onPick(u.id)
                    setOpen(false)
                    setQuery('')
                  }}
                  className="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors"
                >
                  <span className="block truncate">{u.name || u.email}</span>
                  {u.name && <span className="block text-xs text-muted-foreground truncate">{u.email}</span>}
                </button>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default UserSearchPicker
